import { useState, useEffect, useMemo, useCallback } from 'react';
import { analytics, usePrevious } from '@eti/utils';
import { Stack } from '@etg/wings';
import { m as motion, AnimatePresence } from 'framer-motion';
import { useDirection, useProperty, useTranslation } from '@eti/providers';
import { css } from '@eti/styles';
import isFunction from 'lodash/isFunction';
import { CONFIRMATION_MODAL_ACTION_TYPE, CONFIRMATION_MODAL_REDIRECT_TO, CONFIRMATION_MODAL_TYPE, SEATING_PREFERENCE_OPTIONS } from '../utils/constants';
import { findIndexOfSegment, findNextAvailableSegment, getNextSegment, flatBounds, isLastAvailableSegment, getSegment, isFirstAvailableSegment, findPreviousAvailableSegment, getSegmentTravelers, getIdOfFirstUnselectedTraveler, getTotalSelectedSeatsForTravelers, getNonSelectedAvailableSegments, getNonBookedTravelerSeats, getActiveDeck } from '../utils/seatMapContentUtils';
import { useDidUpdateEffect } from '../../custom-hooks/useDidUpdateEffect';
import { getAvailableDeckOptions } from '../utils/cabin';
import SeatSummary from './summary/components/SeatSummary';
import SeatMapHeader from './SeatMapHeader';
import SeatMapMainContent from './SeatMapMainContent';
import Footer from './SeatMapFooter';
import SeatMapConfirmationDialog from './SeatMapConfirmationDialog';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
export const Direction = {
  Forward: 1,
  Backward: -1
};
export const containerStyle = isSummaryShowing => css`
  padding-bottom: 80px;
  position: relative;
  width: 100%;
  ${!isSummaryShowing && 'height: 100%;'}
`;
const motionContainerStyle = (isSummaryShowing, isRTL) => css`
  ${isRTL ? 'right: 0;' : 'left: 0;'}
  ${!isSummaryShowing && 'position: absolute;'}
  top: 0;
  width: 100%;
  ${!isSummaryShowing && 'height: 100%;'}
`;
const motionVariants = {
  enter: {
    opacity: 1
  },
  exit: {
    opacity: 0
  }
};
const SeatMapContent = _ref => {
  var _firstAvailableSegmen, _firstAvailableSegmen2, _firstAvailableSegmen3, _getSelected, _getSegment, _getSegment2;
  let {
    bounds = [],
    onClose,
    onChange,
    getSelected = () => [],
    totalMarkup,
    totalPrice,
    selectedSeats,
    onRemove = () => {},
    travelers,
    isPostBooking,
    onOptOut,
    shouldShowConfirmationModal,
    callback = () => {}
  } = _ref;
  const {
    p
  } = useProperty();
  const {
    t
  } = useTranslation();
  const {
    isRTL
  } = useDirection();
  const isManualNavigationEnabled = p('IbeClient.SeatMap.Segment.Navigation.Manual.Enabled');
  const usePersuasionConfiguration = p('IbeClient.TravelerDetails.OptionalProductsRequireValidation.Enabled');
  const flattenedBounds = useMemo(() => flatBounds(bounds), [bounds]);
  const firstAvailableSegment = getNextSegment(flattenedBounds);
  const [activeSegment, setActiveSegment] = useState((_firstAvailableSegmen = firstAvailableSegment === null || firstAvailableSegment === void 0 ? void 0 : firstAvailableSegment.boundSegmentIndex) !== null && _firstAvailableSegmen !== void 0 ? _firstAvailableSegmen : '0');
  const [activeTraveler, setActiveTraveler] = useState((_firstAvailableSegmen2 = firstAvailableSegment === null || firstAvailableSegment === void 0 || (_firstAvailableSegmen3 = firstAvailableSegment.travelerIds) === null || _firstAvailableSegmen3 === void 0 ? void 0 : _firstAvailableSegmen3[0]) !== null && _firstAvailableSegmen2 !== void 0 ? _firstAvailableSegmen2 : 0);
  const [isSummaryShowing, setIsSummaryShowing] = useState(false);
  const [shouldAnimate, setShouldAnimate] = useState(true);
  const [direction, setDirection] = useState(Direction.Forward);
  const [confirmationModalType, setConfirmationModalType] = useState(CONFIRMATION_MODAL_TYPE.SEAT_MAP_PERSIST_IN_PRODUCT_UNBOOKED_SEGMENTS);
  const [confirmationModalRedirectTo, setConfirmationModalRedirectTo] = useState(CONFIRMATION_MODAL_REDIRECT_TO.DEFAULT);
  const [fromSummary, setFromSummary] = useState(false);
  const [hasMounted, setHasMounted] = useState(false);
  const [showAllSeatsSelectedConfirmation, setShowAllSeatsSelectedConfirmation] = useState();
  const deckLabel = {
    lower: t('Product.SeatMap.DeckSelection.LowerDeck'),
    main: t('Product.SeatMap.DeckSelection.MainDeck'),
    upper: t('Product.SeatMap.DeckSelection.UpperDeck')
  };
  const getAvailableDeckOptionsForSegment = useCallback(index => {
    const currentSegment = flattenedBounds.find(_ref2 => {
      let {
        boundSegmentIndex
      } = _ref2;
      return boundSegmentIndex === index;
    });
    return getAvailableDeckOptions(currentSegment, deckLabel.upper, deckLabel.main, deckLabel.lower);
  }, [deckLabel.lower, deckLabel.main, deckLabel.upper, flattenedBounds]);
  const firstAvailableSegmentActiveDeck = getActiveDeck(getAvailableDeckOptionsForSegment(firstAvailableSegment === null || firstAvailableSegment === void 0 ? void 0 : firstAvailableSegment.boundSegmentIndex));
  const [activeDeck, setActiveDeck] = useState(firstAvailableSegmentActiveDeck);
  const totalSelectedSeats = getTotalSelectedSeatsForTravelers(selectedSeats);
  const nonSelectedAvailableSegments = getNonSelectedAvailableSegments(flattenedBounds, totalSelectedSeats);
  const allSeatsSelected = (nonSelectedAvailableSegments || []).length === 0;
  const handleDeckChange = option => {
    setActiveDeck(option);
  };
  useEffect(() => {
    setHasMounted(true);
  }, []);
  const handleSegmentChange = useCallback(segment => {
    var _travelerIds$;
    const {
      boundSegmentIndex,
      travelerIds
    } = segment;
    analytics.pushToDataLayer({
      event: 'seatingVisible',
      seatingSegment: 'SEGMENTS'
    }, {
      event: 'seatingVisible',
      seatingCurrentSegment: findIndexOfSegment(flattenedBounds, boundSegmentIndex) + 1
    });
    const deckOptions = getAvailableDeckOptionsForSegment(boundSegmentIndex);
    setActiveDeck(getActiveDeck(deckOptions));
    setActiveSegment(boundSegmentIndex);
    setActiveTraveler((_travelerIds$ = travelerIds === null || travelerIds === void 0 ? void 0 : travelerIds[0]) !== null && _travelerIds$ !== void 0 ? _travelerIds$ : 0);
    setIsSummaryShowing(false);
    setFromSummary(false);
  }, [flattenedBounds, getAvailableDeckOptionsForSegment]);
  const handleChangeSeat = _ref3 => {
    let {
      carrier,
      column,
      price,
      row,
      seat,
      markup,
      seatPreference
    } = _ref3;
    return () => {
      const selected = getSelected(activeSegment);
      const existingSeat = selected.find(s => s.value === seat);
      if (existingSeat && !seatPreference) {
        if (isManualNavigationEnabled && selectedSeats) {
          setActiveTraveler(existingSeat.id);
        }
        return;
      }
      onChange(activeSegment, activeTraveler, carrier, column, row, seat, price, markup, seatPreference);
    };
  };
  const changeSegmentWithDirection = useCallback((segment, dir) => {
    setDirection(dir);
    setShouldAnimate(true);
    requestAnimationFrame(() => {
      handleSegmentChange(segment);
    });
  }, [handleSegmentChange]);
  const isFirstSegment = isFirstAvailableSegment(flattenedBounds, activeSegment);
  const handlePreviousSegmentClick = () => {
    if (isFirstAvailableSegment(flattenedBounds, activeSegment)) {
      return;
    }
    if (isSummaryShowing) {
      setIsSummaryShowing(false);
      setFromSummary(false);
      return;
    }
    const previousSegment = findPreviousAvailableSegment(flattenedBounds, activeSegment);
    if (previousSegment) {
      changeSegmentWithDirection(previousSegment, Direction.Backward);
    }
  };
  const handleChangeTraveler = traveler => () => {
    setActiveTraveler(traveler);
    if (fromSummary) {
      setFromSummary(false);
    }
  };
  const handleNextSegmentClick = useCallback(() => {
    analytics.pushToDataLayer({
      event: 'seatingVisible',
      seatingSegment: 'CONTINUE'
    });
    if (isSummaryShowing) {
      return;
    }
    const isLastAvailableSegmentActive = isLastAvailableSegment(flattenedBounds, activeSegment);
    if (isLastAvailableSegmentActive) {
      if (usePersuasionConfiguration && isManualNavigationEnabled && !allSeatsSelected) {
        setConfirmationModalType(CONFIRMATION_MODAL_TYPE.SEAT_MAP_PERSIST_IN_PRODUCT_UNBOOKED_SEGMENTS);
        setConfirmationModalRedirectTo(CONFIRMATION_MODAL_REDIRECT_TO.SUMMARY);
        setShowAllSeatsSelectedConfirmation(true);
      } else {
        setIsSummaryShowing(true);
        setFromSummary(false);
      }
    } else {
      const nextSegment = findNextAvailableSegment(flattenedBounds, activeSegment);
      if (nextSegment) {
        changeSegmentWithDirection(nextSegment, Direction.Forward);
      }
    }
    setShouldAnimate(true);
  }, [activeSegment, allSeatsSelected, changeSegmentWithDirection, flattenedBounds, isManualNavigationEnabled, isSummaryShowing, usePersuasionConfiguration]);
  const handleToggleSeatSelect = (boundSegmentIndex, traveler) => () => {
    setActiveSegment(boundSegmentIndex);
    setActiveTraveler(traveler);
    const deckOptions = getAvailableDeckOptionsForSegment(boundSegmentIndex);
    setActiveDeck(getActiveDeck(deckOptions));
    setIsSummaryShowing(false);
    setFromSummary(true);
  };
  const handleRemoveSeat = (segment, traveler) => () => {
    onRemove(segment, traveler);
  };
  const handleOnClose = function (data) {
    if (data === void 0) {
      data = selectedSeats;
    }
    analytics.pushToDataLayer([{
      event: 'seatingVisible',
      seatingSegment: 'BOOK'
    }, {
      event: 'seatingVisible',
      seatingOnBook: 'YES'
    }]);
    if (isFunction(onClose)) {
      const returnedFunction = onClose(data);
      if (isFunction(returnedFunction)) {
        returnedFunction();
      }
    }
    if (isFunction(callback)) {
      callback(selectedSeats);
    }
  };
  const handleOnOptOut = async () => {
    analytics.pushToDataLayer({
      event: 'seatingVisible',
      seatingSegment: 'NO'
    });
    if (isFunction(onOptOut)) {
      await onOptOut();
    }
    if (isFunction(callback)) {
      callback([]);
    }
  };
  const handleCloseButton = () => {
    setConfirmationModalType(CONFIRMATION_MODAL_TYPE.SEAT_MAP_PERSIST_IN_PRODUCT);
    if (usePersuasionConfiguration && shouldShowConfirmationModal && p('IbeClient.TravelerDetails.Modal') === 'SEATMAP') {
      setConfirmationModalRedirectTo(CONFIRMATION_MODAL_REDIRECT_TO.PAYMENT);
      setShowAllSeatsSelectedConfirmation(true);
    } else {
      handleOnOptOut();
    }
  };
  const handleConfirmationSelection = (action, segment) => {
    setShowAllSeatsSelectedConfirmation(false);
    if (action === CONFIRMATION_MODAL_ACTION_TYPE.SEAT_MAP_PERSIST_ACTION_DISMISS) {
      if (totalPrice === 0 && isPostBooking) {
        handleOnOptOut();
        return;
      }
      if (confirmationModalRedirectTo === CONFIRMATION_MODAL_REDIRECT_TO.SUMMARY) {
        setIsSummaryShowing(true);
        setFromSummary(false);
      } else {
        handleOnOptOut();
      }
    } else {
      if (!segment) {
        return;
      }
      const nonBookedTravelerSeats = getNonBookedTravelerSeats(flattenedBounds.find(seg => seg.boundSegmentIndex === segment), totalSelectedSeats);
      handleToggleSeatSelect(segment, nonBookedTravelerSeats[0])();
    }
  };
  useEffect(() => {
    analytics.pushToDataLayer({
      event: 'seatingVisible',
      seatingCurrentSegment: activeSegment
    });
  }, [activeSegment]);
  const prevSelectedSeatsInSegmentCount = usePrevious((_getSelected = getSelected(activeSegment)) === null || _getSelected === void 0 ? void 0 : _getSelected.length);
  useDidUpdateEffect(() => {
    var _segment$travelerIds;
    const segment = flattenedBounds.find(s => s.boundSegmentIndex === activeSegment);
    if (!segment) {
      return;
    }
    const selectedSeatsInSegmentCount = getSelected(activeSegment).length;
    const allSelected = selectedSeatsInSegmentCount === ((_segment$travelerIds = segment.travelerIds) === null || _segment$travelerIds === void 0 ? void 0 : _segment$travelerIds.length);
    const isSpecialType = segment.__typename === SEATING_PREFERENCE_OPTIONS.SELL_SEATING_PREFERENCE || segment.__typename === SEATING_PREFERENCE_OPTIONS.SELL_SEATING_BESIDE;
    if (!isManualNavigationEnabled && (selectedSeatsInSegmentCount !== prevSelectedSeatsInSegmentCount && allSelected || isSpecialType)) {
      handleNextSegmentClick();
    }
    const nextUnselectedTraveler = getIdOfFirstUnselectedTraveler(getSegmentTravelers(travelers, flattenedBounds, activeSegment), getSelected(activeSegment), activeTraveler);
    if (nextUnselectedTraveler !== null) {
      handleChangeTraveler(nextUnselectedTraveler)();
    }
  }, [selectedSeats]);
  analytics.useLogMouseFlowTagEffect('Seatmap 4.0');
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(SeatMapHeader, {
      currentSegmentData: getSegment(flattenedBounds, activeSegment),
      currentSegmentIndex: findIndexOfSegment(flattenedBounds, activeSegment) + 1,
      from: ((_getSegment = getSegment(flattenedBounds, activeSegment)) === null || _getSegment === void 0 || (_getSegment = _getSegment.from) === null || _getSegment === void 0 ? void 0 : _getSegment.city) || '',
      handleCloseButton: handleCloseButton,
      isSummaryShowing: isSummaryShowing,
      to: ((_getSegment2 = getSegment(flattenedBounds, activeSegment)) === null || _getSegment2 === void 0 || (_getSegment2 = _getSegment2.to) === null || _getSegment2 === void 0 ? void 0 : _getSegment2.city) || '',
      totalSegmentsCount: flattenedBounds.length
    }), /*#__PURE__*/_jsxs(Stack, {
      children: [/*#__PURE__*/_jsx("div", {
        className: containerStyle(isSummaryShowing),
        children: /*#__PURE__*/_jsx(AnimatePresence, {
          children: /*#__PURE__*/_jsx(motion.div, {
            animate: shouldAnimate ? {
              x: 0,
              opacity: 1
            } : false,
            className: motionContainerStyle(isSummaryShowing, isRTL),
            exit: {
              x: direction === Direction.Forward ? '-100%' : '100%',
              opacity: 0
            },
            initial: hasMounted ? {
              x: direction === Direction.Forward ? '100%' : '-100%',
              opacity: 0
            } : false,
            transition: {
              type: 'tween',
              duration: 0.5,
              ease: [0.4, 0, 0.2, 1]
            },
            children: isSummaryShowing ? /*#__PURE__*/_jsx(SeatSummary, {
              getSelected: getSelected,
              onRemoveSeat: handleRemoveSeat,
              onToggleSeatSelect: handleToggleSeatSelect,
              segments: flattenedBounds,
              totalMarkup: totalMarkup,
              totalPrice: totalPrice,
              travelers: travelers
            }) : /*#__PURE__*/_jsx(SeatMapMainContent, {
              activeSegment: activeSegment,
              activeTraveler: activeTraveler,
              deckOptions: getAvailableDeckOptionsForSegment(activeSegment),
              getSelected: getSelected,
              onChange: handleChangeSeat,
              onChangeTraveler: handleChangeTraveler,
              onDeckChange: handleDeckChange,
              onRemoveSeat: handleRemoveSeat,
              seatTravelers: getSegmentTravelers(travelers, flattenedBounds, activeSegment),
              segments: flattenedBounds,
              selectedDeck: activeDeck,
              selectedSeats: getSelected(activeSegment)
            })
          }, `${activeSegment}-${isSummaryShowing}`)
        })
      }), /*#__PURE__*/_jsx(Footer, {
        areAllSeatsSelected: true,
        handlePreviousSegmentClick: handlePreviousSegmentClick,
        isFirstSegment: isFirstSegment,
        isPostBooking: isPostBooking,
        onClose: handleOnClose,
        onSaveAndContinue: handleNextSegmentClick,
        selectNoOption: handleOnOptOut,
        selectedSeats: selectedSeats,
        setShowAllSeatsSelectedConfirmation: null,
        shouldShowSummary: isSummaryShowing,
        totalPrice: totalPrice
      })]
    }), /*#__PURE__*/_jsx(AnimatePresence, {
      children: showAllSeatsSelectedConfirmation && /*#__PURE__*/_jsx(motion.div, {
        animate: "enter",
        exit: "exit",
        transition: {
          type: 'tween',
          duration: 0.3
        },
        variants: motionVariants,
        children: /*#__PURE__*/_jsx(SeatMapConfirmationDialog, {
          onSelection: handleConfirmationSelection,
          redirectTo: confirmationModalRedirectTo,
          segments: nonSelectedAvailableSegments,
          type: confirmationModalType
        })
      }, "seatMapContent-allSeatsSelectedConfirmation")
    })]
  });
};
export default SeatMapContent;
//# sourceMappingURL=SeatMapContent.js.map